import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type {
  ExternalLinkDto,
  ExternalLinkPlatformOptionDto,
  ExternalLinkRequestDto,
} from '@kardume/types';
import {
  EXTERNAL_LINK_MAX_COUNT,
  EXTERNAL_LINK_MAX_LABEL_LENGTH,
  EXTERNAL_LINK_MAX_URL_LENGTH,
  EXTERNAL_LINK_OTHER_PLATFORM,
} from '@kardume/types';
import ActionButton from './ActionButton';
import Button from './Button';
import Select from './Select';
import type { SelectOptionGroup } from './Select';
import TextInput from './TextInput';

/** Validation messages for one row, keyed by the field they belong to. */
export interface ExternalLinkFieldErrors {
  platform?: string;
  customLabel?: string;
  url?: string;
}

/** A row the user opened but never touched - not a channel, just an empty slot. */
const isBlankLink = (link: ExternalLinkRequestDto): boolean =>
  !link.platform && !link.url.trim() && !(link.customLabel ?? '').trim();

const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

const isParsableUrl = (candidate: string): boolean => {
  try {
    return !!new URL(candidate);
  } catch {
    return false;
  }
};

/**
 * Mirrors the core's validation so the user sees the problem before the request.
 *
 * Deliberately never stricter than the backend: a URL without a scheme is valid
 * (the core prefixes `https://`), only a foreign scheme is refused, and the
 * label is checked only where the catalog says it is required.
 */
export const validateExternalLinks = (
  links: ExternalLinkRequestDto[],
  platforms: ExternalLinkPlatformOptionDto[]
): ExternalLinkFieldErrors[] => {
  const seen = new Set<string>();

  return links.map((link) => {
    const errors: ExternalLinkFieldErrors = {};
    if (isBlankLink(link)) return errors;

    if (!link.platform) {
      errors.platform = 'Selecione o canal.';
    } else if (link.platform !== EXTERNAL_LINK_OTHER_PLATFORM) {
      if (seen.has(link.platform)) errors.platform = 'Este canal já foi adicionado.';
      seen.add(link.platform);
    }

    if (platforms.find((option) => option.value === link.platform)?.requiresCustomLabel) {
      const customLabel = (link.customLabel ?? '').trim();
      if (!customLabel) errors.customLabel = 'Informe o nome do canal.';
      else if (customLabel.length > EXTERNAL_LINK_MAX_LABEL_LENGTH) {
        errors.customLabel = `No máximo ${EXTERNAL_LINK_MAX_LABEL_LENGTH} caracteres.`;
      }
    }

    const url = link.url.trim();
    const scheme = url.match(SCHEME_PATTERN)?.[0].slice(0, -1).toLowerCase();
    if (!url) {
      errors.url = 'Informe o endereço do canal.';
    } else if (url.length > EXTERNAL_LINK_MAX_URL_LENGTH) {
      errors.url = `No máximo ${EXTERNAL_LINK_MAX_URL_LENGTH} caracteres.`;
    } else if (scheme && scheme !== 'http' && scheme !== 'https') {
      errors.url = 'Use um endereço http:// ou https://.';
    } else if (!isParsableUrl(scheme ? url : `https://${url}`)) {
      errors.url = 'Endereço inválido - confira o link.';
    }

    return errors;
  });
};

/** True when any row still has something to fix. */
export const hasExternalLinkErrors = (errors: ExternalLinkFieldErrors[]): boolean =>
  errors.some((row) => !!row.platform || !!row.customLabel || !!row.url);

/** Rebuild the editable list from what the API returned. */
export const externalLinksToRequest = (links?: ExternalLinkDto[] | null): ExternalLinkRequestDto[] =>
  (links ?? []).map((link) => ({
    platform: link.platform,
    url: link.url,
    // For every platform but OTHER the `label` is the enum's own text, which the
    // core ignores on write; only under OTHER is it what the merchant typed.
    customLabel: link.platform === EXTERNAL_LINK_OTHER_PLATFORM ? link.label : undefined,
  }));

/** Trim and drop the empty rows, producing the list the core will store as-is. */
export const externalLinksToPayload = (links: ExternalLinkRequestDto[]): ExternalLinkRequestDto[] =>
  links
    .filter((link) => !!link.platform && !!link.url.trim())
    .map((link) => ({
      platform: link.platform,
      url: link.url.trim(),
      customLabel: (link.customLabel ?? '').trim() || undefined,
    }));

const Card = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Row = styled.div<{ $withCustomLabel: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$withCustomLabel ? '200px 200px 1fr auto' : '200px 1fr auto')};
  align-items: start;
  gap: 0.6rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  /* Keeps the buttons level with the 42px fields, which sit above their error text. */
  min-height: 42px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Muted = styled.span`
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

export interface ExternalLinksInputProps {
  /** Ordered list being edited; the order is the order shown on the store page. */
  value: ExternalLinkRequestDto[];
  onChange: (links: ExternalLinkRequestDto[]) => void;
  /** Platform catalog from the core. Never hardcode it - this is the only list. */
  platforms: ExternalLinkPlatformOptionDto[];
  platformsLoading?: boolean;
  disabled?: boolean;
  max?: number;
}

/**
 * Editor for the store's external sales channels, shared by the merchant admin
 * and the backoffice merchant form.
 *
 * Validation is computed here so each field shows its own message while typing;
 * `validateExternalLinks` is exported so the screen that owns the submit button
 * can gate on the very same rules instead of writing a second set.
 */
export const ExternalLinksInput: React.FC<ExternalLinksInputProps> = ({
  value,
  onChange,
  platforms,
  platformsLoading,
  disabled,
  max = EXTERNAL_LINK_MAX_COUNT,
}) => {
  const theme = useTheme();
  const errors = useMemo(() => validateExternalLinks(value, platforms), [value, platforms]);

  const usedPlatforms = useMemo(
    () =>
      value
        .map((link) => link.platform)
        .filter((platform) => !!platform && platform !== EXTERNAL_LINK_OTHER_PLATFORM),
    [value]
  );

  /**
   * Groups are rebuilt per row because "already used" depends on the row: the
   * core refuses two links to the same platform, but the row's own platform has
   * to stay selectable or the select would show an empty box.
   */
  const groupsFor = (rowIndex: number): SelectOptionGroup[] => {
    const groups: SelectOptionGroup[] = [];
    platforms.forEach((platform) => {
      const option = {
        value: platform.value,
        label: platform.label,
        disabled: platform.value !== value[rowIndex].platform && usedPlatforms.includes(platform.value),
      };
      const group = groups.find((it) => it.label === platform.groupLabel);
      if (group) group.options.push(option);
      else groups.push({ label: platform.groupLabel, options: [option] });
    });
    return groups;
  };

  /** A platform the catalog no longer offers must still show, or editing would silently drop it. */
  const placeholderFor = (link: ExternalLinkRequestDto) =>
    link.platform && !platforms.some((platform) => platform.value === link.platform)
      ? [{ value: link.platform, label: link.platform }]
      : [{ value: '', label: 'Selecione o canal' }];

  const patch = (index: number, changes: Partial<ExternalLinkRequestDto>) =>
    onChange(value.map((link, i) => (i === index ? { ...link, ...changes } : link)));

  const move = (index: number, offset: number) => {
    const next = [...value];
    const [moved] = next.splice(index, 1);
    next.splice(index + offset, 0, moved);
    onChange(next);
  };

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  const add = () => onChange([...value, { platform: '', url: '', customLabel: '' }]);

  const canAdd = !disabled && value.length < max;

  return (
    <Card>
      {platformsLoading && value.length === 0 && <Muted>Carregando canais...</Muted>}
      {!platformsLoading && value.length === 0 && <Muted>Nenhum canal cadastrado.</Muted>}

      {value.map((link, index) => {
        const option = platforms.find((it) => it.value === link.platform);
        const rowErrors = errors[index] ?? {};
        return (
          <Row key={index} $withCustomLabel={!!option?.requiresCustomLabel}>
            <Select
              aria-label={`Canal ${index + 1}`}
              disabled={disabled || platformsLoading}
              value={link.platform}
              error={rowErrors.platform}
              options={placeholderFor(link)}
              groups={groupsFor(index)}
              onChange={(e) => patch(index, { platform: e.target.value })}
            />
            {option?.requiresCustomLabel && (
              <TextInput
                aria-label={`Nome do canal ${index + 1}`}
                placeholder="Nome do canal"
                disabled={disabled}
                maxLength={EXTERNAL_LINK_MAX_LABEL_LENGTH}
                value={link.customLabel ?? ''}
                error={rowErrors.customLabel}
                onChange={(e) => patch(index, { customLabel: e.target.value })}
              />
            )}
            <TextInput
              aria-label={`Endereço do canal ${index + 1}`}
              placeholder="https://"
              disabled={disabled}
              maxLength={EXTERNAL_LINK_MAX_URL_LENGTH}
              value={link.url}
              error={rowErrors.url}
              onChange={(e) => patch(index, { url: e.target.value })}
            />
            <RowActions>
              <ActionButton
                type="button"
                title="Subir"
                disabled={disabled || index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUp size={15} />
              </ActionButton>
              <ActionButton
                type="button"
                title="Descer"
                disabled={disabled || index === value.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDown size={15} />
              </ActionButton>
              <ActionButton
                type="button"
                title="Remover"
                color={theme.error}
                disabled={disabled}
                onClick={() => remove(index)}
              >
                <Trash2 size={15} />
              </ActionButton>
            </RowActions>
          </Row>
        );
      })}

      <Footer>
        <Button type="button" variant="outline" disabled={!canAdd} onClick={add}>
          <Plus size={16} /> Adicionar canal
        </Button>
        <Muted>
          {value.length}/{max} canais
        </Muted>
      </Footer>
    </Card>
  );
};

export default ExternalLinksInput;
